import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { marginSizes } from '@jarvis.oddle/core/theme/default'
import { isEmpty } from 'src/utils/string'
import { P } from '@jarvis.oddle/core/components/Typo'
import Div from '@jarvis.oddle/core/components/Div'
import Form, { FormContext } from '@jarvis.oddle/core/components/Form'
import Divider from '@jarvis.oddle/core/components/Divider'
import Collapsible from '@jarvis.oddle/core/components/Collapsible'
import {
  carrierTypes,
  invoiceOptions
} from '@jarvis.oddle/core/tools/paymentInvoice'
import {
  InlineNotification,
  notificationStates
} from '@jarvis.oddle/core/components/InlineNotification'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { CardGroup, CardHeader, InputContainer, Section } from './Elements'
import useAppSelector from 'src/hooks/useAppSelector'
import { selectDetailMenuSite } from 'src/redux/meta'

const StyledFormSelectWithLabel = styled(Form.SelectWithLabel)`
  && {
    height: 60px;
  }
`

interface InvoiceInformationProps {
  invoiceOption: Oms.V4.InvoiceOption
}

const InvoiceInformation = ({
  invoiceOption
}: InvoiceInformationProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const { values, errors } = React.useContext(FormContext)
  const metaState = useAppSelector(selectDetailMenuSite)
  const { features } = metaState || {}

  const selectedCarrierType = values.carrierType && values.carrierType.value

  if (invoiceOption === invoiceOptions.nonTW) return null

  const isUnifiedBusinessNumberEntered =
    values.unifiedBusinessNumber && !errors.unifiedBusinessNumber

  const carrierTypeOptions = [
    { label: t('E-invoice Carrier'), value: carrierTypes.greenWorldEInvoice },
    {
      label: t('Citizen Digital Certificate'),
      value: carrierTypes.citizenDigitalCertificate,
      disabled: isUnifiedBusinessNumberEntered
    },
    { label: t('Cellphone Barcode'), value: carrierTypes.cellphoneBarcode }
  ]

  const onInputChange = ({
    name,
    value,
    setFieldValue,
    shouldValidateOnChange
  }) => {
    const nextValue = (value || '').toUpperCase()
    setFieldValue(name, nextValue, shouldValidateOnChange)
  }

  return (
    <React.Fragment>
      <Section>
        <CardHeader>{t('Invoice Information')}</CardHeader>
        <P>
          {t(
            'If tax reporting is required, please enter the unified business number and organisation name below.'
          )}
          {!features?.enableOptionsForInvoiceInstructions && t(
            'Your uniform invoice shall be issued by the restaurant upon order fulfilment.'
          )}
        </P>
        {invoiceOption === invoiceOptions.eInvoice && (
          <Div width='100%' marginBottom={marginSizes.medium}>
            <InlineNotification
              lightTheme
              state={notificationStates.informative}
              text={t(
                '• Please ensure that the unified business number and your organisation name are correctly filled before submitting your order. No changes will be accepted once the e-invoice is issued.\n• Please place separate orders if separate e-invoices are required. The e-invoices are issued by an automated system which is unable to split and issue separate e-invoices for an order.\n• We are unable to issue e-invoice with any specified food item name, order value, or issuance date. The format of the e-invoice will be as follow - food items will be consolidated as a single line item; order value shall be based on the actual value of the order; issuance date shall be based on the actual date of e-invoice issuance.'
              )}
            />
          </Div>
        )}
        <CardGroup>
          <InputContainer>
            <Form.Input
              type='text'
              name='unifiedBusinessNumber'
              label={t('Unified Business No.')}
            />
          </InputContainer>
          <InputContainer>
            <Form.Input
              type='text'
              name='organisationName'
              label={t('Organisation Name (Optional)')}
            />
          </InputContainer>
        </CardGroup>
        {invoiceOption === invoiceOptions.eInvoice && (
          <Collapsible show={isEmpty(values.unifiedBusinessNumber)}>
            <P bold>{t('E-invoice Carrier Type')}</P>
            <CardGroup>
              <InputContainer>
                <StyledFormSelectWithLabel
                  name='carrierType'
                  label={t('E-invoice Carrier Type')}
                  options={carrierTypeOptions}
                />
              </InputContainer>
              {(selectedCarrierType ===
                carrierTypes.citizenDigitalCertificate ||
                selectedCarrierType === carrierTypes.cellphoneBarcode) && (
                <InputContainer>
                  <Form.Input
                    type='text'
                    name='carrierNumber'
                    label={
                      selectedCarrierType === carrierTypes.cellphoneBarcode
                        ? t('Cellphone Barcode')
                        : t('Citizen Digital Certificate')
                    }
                    onChange={onInputChange}
                  />
                </InputContainer>
              )}
            </CardGroup>
          </Collapsible>
        )}
      </Section>
      <Divider />
    </React.Fragment>
  )
}

InvoiceInformation.propTypes = {
  invoiceOption: PropTypes.oneOf(Object.values(invoiceOptions))
}

export default InvoiceInformation
